import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CandidateDetail,
  CandidateDetailV2,
} from "../../../../models/candidate-detail.model";
import WageClaim from "../../../wage-claim/wage-claim";
import { InformationLink, SectionBlock, Title } from "../../talent.styled";
import { Skill, SkillsList } from "../candidate-skills/candidate-skills.styled";
import TooltipWarning from "../../../tooltip-warning";

const verifyEmptyString = (value?: string): boolean => {
  return !!value && value !== "";
};

interface SpotlightProps {
  candidate?: CandidateDetail | null;
  candidateDetail?: CandidateDetailV2 | null;
  tech?: string;
  currencyIso?: string;
}

const Spotlight: React.FC<SpotlightProps> = ({
  candidate,
  candidateDetail,
  tech,
  currencyIso,
}) => {
  const { t } = useTranslation("private-home-page");

  const experienceTime = useMemo((): string | undefined => {
    if (verifyEmptyString(candidateDetail?.experience_time_tech))
      return candidateDetail?.experience_time_tech;
    if (verifyEmptyString(candidate?.experience_time_tech))
      return candidate?.experience_time_tech;
    if (verifyEmptyString(candidate?.experience_time))
      return candidate?.experience_time;
    return undefined;
  }, [
    candidate?.experience_time_tech,
    candidate?.experience_time,
    candidateDetail?.experience_time_tech,
  ]);

  const getOriginalWageClaim = useCallback(
    (candidate: CandidateDetail) => {
      return candidate?.wage_claims?.[0]?.brl
        ? t("wage-claim-converted-original", {
            originalValue: parseFloat(
              candidate.wage_claims[0].brl
            ).toLocaleString(),
          })
        : t("wage-claim-converted");
    },
    [t]
  );

  return (
    <SectionBlock>
      <Title>{t("talent.emphasis")}</Title>
      <SkillsList>
        {candidate?.city && (
          <Skill>
            {candidate?.city.name}/{candidate?.city.state}
          </Skill>
        )}
        {candidate &&
          candidate.diversity?.length > 0 &&
          candidate.diversity.map((diversity, index) =>
            diversity.name === "Prefiro não dizer" ||
            !verifyEmptyString(diversity.name) ? null : (
              <Skill key={`diversity-${index}`}>{diversity.name}</Skill>
            )
          )}

        {experienceTime && <Skill>{experienceTime}</Skill>}

        <Skill>
          <WageClaim
            value={Number(candidate?.wage_claim)}
            currencyIso={currencyIso}
          />
          {candidate?.wage_claim_was_converted && (
            <TooltipWarning
              message={getOriginalWageClaim(candidate)}
              position="left"
              marginLeft=".1rem"
            />
          )}
        </Skill>

        {candidate?.hire_types &&
          candidate.hire_types.map(
            (hireType, index) =>
              verifyEmptyString(hireType.name) && (
                <Skill key={`hireTypes-${index}`}>{hireType}</Skill>
              )
          )}

        {candidate?.remote && <Skill>{t("talent.remoteWork")}</Skill>}

        {candidate?.work_model_presencial && (
          <Skill>{t("talent.presentialWork")}</Skill>
        )}

        {candidate?.work_model_hybrid && (
          <Skill>{t("talent.hybridWork")}</Skill>
        )}

        {candidate?.track_tests && (
          <InformationLink target="_blank" href={candidate?.track_tests}>
            {t("talent.viewChallenge")}
          </InformationLink>
        )}
      </SkillsList>
    </SectionBlock>
  );
};

export default Spotlight;
